// react
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

// mui
import { Box, CircularProgress } from '@mui/material';

// context
import { AccountRole, useTenant } from 'context';

// components
import { OrganiserPage } from '@karehero/llama/pwa/pages/OrganiserPage/OrganiserPage';
import { OrganiserAdminBlockedPage } from '@karehero/llama/pwa/pages/OrganiserAdminBlockedPage/OrganiserAdminBlockedPage';
import { CreateEntryDialog } from '@karehero/llama/pwa/organisms/EntryDialog/CreateEntryDialog';
import EditEntryDialog, {
  EditEntryState,
} from '@karehero/llama/pwa/organisms/EntryDialog/EditEntryDialog';

// hooks
import { useTracking, TrackingEvent } from 'hooks/useTracking';
import { useFeatureFlags } from 'hooks/useFeatureFlags';

// router
import { ROUTES } from 'router/routes';

// types
import { OrganiseEntryStatus } from '@karehero/models';

// theme
import { useOrganiserActions } from 'hooks/useOrganiserActions';
import { useOrganiserCareCircles } from 'hooks/useOrganiserCareCircles';

const OrganiserLoader = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
      }}
    >
      <CircularProgress />
    </Box>
  );
};

interface OrganiserState {
  mode?: 'create' | 'edit' | null;
  entryId?: string | null;
}

export const Organiser = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();

  // Tracking
  const { track } = useTracking();

  // Feature flags
  const { flags } = useFeatureFlags();

  // Local state
  const [showCompleted, setShowCompleted] = useState(false);
  const [dialog, setDialog] = useState<OrganiserState>({});

  // Block admins from accessing the organiser
  const isAdmin = tenant.accountRole === AccountRole.ADMIN;

  const {
    isLoading: isLoadingCareCircles,
    careCircles,
    activeCircles,
    activeCareCircleIds,
    handleActiveCareCircleIdsChange,
  } = useOrganiserCareCircles({
    skip: isAdmin,
  });

  const {
    isPerformingAction,
    isLoading: isLoadingEntries,
    entries,
    calendars,
    findRawEntry,
    createEntry,
    updateEntry,
    deleteEntry,
    cancelOccurence,
    deleteRepeatingTask,
    changeStatus,
  } = useOrganiserActions(activeCircles, {
    skip: isAdmin || !activeCircles || activeCircles.length === 0,
  });

  const entry = useMemo(() => {
    if (!dialog.entryId) return;
    return findRawEntry(dialog.entryId);
  }, [findRawEntry, dialog.entryId]);

  // Track page view and empty state once data loads
  useEffect(() => {
    if (isLoadingCareCircles || isLoadingEntries) return;

    track(TrackingEvent.OrganiserViewed, {
      careCircleCount: careCircles.length,
      entryCount: entries.length,
    });

    if (entries.length === 0) {
      track(TrackingEvent.OrganiserEmpty, {
        careCircleCount: careCircles.length,
      });
    }
  }, [isLoadingCareCircles, isLoadingEntries]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handlers
  const handleEntryClick = useCallback(
    (entryId: string) => {
      const entry = findRawEntry(entryId);
      if (!entry) return;

      track(TrackingEvent.OrganiserDialogOpened, {
        mode: 'edit',
        isRecurring: !!entry.recurringEventId,
      });
      setDialog({
        mode: 'edit',
        entryId: entryId,
      });
    },
    [findRawEntry, track],
  );

  const handleAddEntry = useCallback(() => {
    track(TrackingEvent.OrganiserDialogOpened, { mode: 'create' });

    setDialog({
      mode: 'create',
    });
  }, [track]);

  const handleDialogComplete = useCallback(() => setDialog({}), [setDialog]);

  const handleDialogCancelled = useCallback(() => {
    track(TrackingEvent.OrganiserDialogCancelled, {
      mode: dialog.mode,
    });
    setDialog({});
  }, [track]);

  const handleStatusChange = useCallback(
    async (entryId: string, status: OrganiseEntryStatus) => {
      await changeStatus(entryId, status);
    },
    [changeStatus],
  );

  const handleUpdate = useCallback(
    async (entry: EditEntryState) => {
      if (!entry.title.trim() || !entry.calendarId) return;

      await updateEntry(entry);

      handleDialogComplete();
    },
    [updateEntry, handleDialogComplete],
  );

  const handleCreate = useCallback(
    async (entry: any) => {
      if (!entry.title.trim() || !entry.calendarId) return;

      await createEntry(entry);

      handleDialogComplete();
    },
    [createEntry, handleDialogComplete],
  );

  const handleDelete = useCallback(
    async (calendarId: string, entryId: string) => {
      if (!entryId || !calendarId) return;

      await deleteEntry(calendarId, entryId);

      handleDialogComplete();
    },
    [deleteEntry, handleDialogComplete],
  );

  const handleCancelOccurrence = useCallback(
    async (
      calendarId: string,
      recurringEventId?: string | null,
      occurrenceDate?: string | null,
    ) => {
      if (!recurringEventId || !occurrenceDate) return;

      await cancelOccurence(calendarId, recurringEventId, occurrenceDate);

      handleDialogComplete();
    },
    [cancelOccurence, handleDialogComplete],
  );

  const handleDeleteRepeatingTask = useCallback(
    async (calendarId: string, recurringEventId?: string | null) => {
      if (!recurringEventId) return;

      await deleteRepeatingTask(calendarId, recurringEventId);

      handleDialogComplete();
    },
    [deleteRepeatingTask, handleDialogComplete],
  );

  const onToggleShowCompleted = useCallback(() => {
    setShowCompleted((prev) => {
      track(TrackingEvent.OrganiserHideCompletedToggled, {
        isHidden: prev,
      });
      return !prev;
    });
  }, [setShowCompleted, track]);

  const onShowMore = useCallback(
    (currentlyVisible: number, totalUpcoming: number) => {
      track(TrackingEvent.OrganiserShowMoreClicked, {
        currentlyVisible,
        totalUpcoming,
      });
    },
    [track],
  );

  // Behind isActionsV2, send members to the new v2 actions experience.
  if (flags.isActionsV2) {
    return <Navigate to={ROUTES.V2_ACTIONS} replace />;
  }

  if (isAdmin) {
    return <OrganiserAdminBlockedPage onGoBack={() => navigate(-1)} />;
  }

  if (isLoadingCareCircles || isLoadingEntries) {
    return <OrganiserLoader />;
  }

  return (
    <OrganiserPage
      careCircles={careCircles}
      activeCareCircleIds={activeCareCircleIds}
      onActiveCareCircleIdsChange={handleActiveCareCircleIdsChange}
      entries={entries}
      onStatusChange={handleStatusChange}
      onEntryClick={handleEntryClick}
      onAddEntry={handleAddEntry}
      onEntryLinkClick={(link) => navigate(link)}
      showCompleted={showCompleted}
      onToggleShowCompleted={onToggleShowCompleted}
      onShowMore={onShowMore}
      dialogSlot={
        dialog.mode === 'edit' && entry ? (
          <EditEntryDialog
            isOpen={dialog.mode === 'edit'}
            onClose={handleDialogCancelled}
            entry={entry}
            calendars={calendars}
            onSave={handleUpdate}
            onDelete={handleDelete}
            onCancelOccurrence={handleCancelOccurrence}
            onDeleteSeries={handleDeleteRepeatingTask}
            isSaving={isPerformingAction}
          />
        ) : (
          <CreateEntryDialog
            isOpen={dialog.mode === 'create'}
            onClose={handleDialogCancelled}
            calendars={calendars}
            onSave={handleCreate}
            isSaving={isPerformingAction}
          />
        )
      }
    />
  );
};

Organiser.displayName = 'Organiser';
export default Organiser;
